<script setup>
/**
 * The card that introduces a set of prompts, before any form is drawn.
 *
 * ONE COMPONENT, THREE PLACES: the required wall at /prompt ('wall'), the
 * optional nudge docked over whatever page someone is on ('docked'), and the
 * live preview in the super admin console. The console preview renders THIS, so
 * it cannot drift from what a member actually sees: an author is looking at the
 * product, not at a drawing of it.
 *
 * The MODE is the SET's, derived by the server from the forms it holds. A
 * required card has one button and no way out: no close, no "later", and
 * clicking outside does nothing. That is not decoration: letting someone
 * decline the card would walk them straight past the required form behind it.
 *
 * The WALL describes the whole run it opens. It lists the forms that follow and,
 * when the run asks for the terms, says so in its own box, because a card that
 * talks about the terms and then asks for a county two pages later reads as a
 * trick (UAT, 27 Sep 2026).
 */
import {computed} from 'vue';
import {promptCardSteps} from '~/composables/useUserFormPromptCard';

const props = defineProps({
  /** {title, line, mode, steps, termsLine}: the `card` block from GET userFormPrompts/pending. */
  card: {type: Object, required: true},
  /**
   * 'wall' for the required page and its console preview, 'docked' for the
   * optional nudge. 'centred' is the old name for the wall and still accepted.
   */
  placement: {type: String, default: 'wall'},
  busy: {type: Boolean, default: false},
  /** Preview only: draws the card without wiring the buttons to anything. */
  inert: {type: Boolean, default: false},
});

const emit = defineEmits(['continue', 'later', 'never']);

const required = computed(() => props.card?.mode === 'required');
const title = computed(() => props.card?.title || 'One quick thing');
const line = computed(() => props.card?.line || null);
const wall = computed(() => props.placement !== 'docked');
const steps = computed(() => promptCardSteps(props.card));
const termsLine = computed(() => props.card?.termsLine || null);

const act = (event) => {
  if (props.inert || props.busy) return;
  emit(event);
};
</script>

<template>
  <!-- The required page: a proper screen of its own, not the nudge made big. -->
  <div v-if="wall" class="picw" :class="{'pic--inert': inert}" data-testid="prompt-intro-wall">
    <div class="picw-logo"><img src="/images/icon_red.png" alt="Tap Leagues"/></div>
    <h1 class="picw-title">{{ title }}</h1>
    <p v-if="line" class="picw-line">{{ line }}</p>

    <div v-if="termsLine" class="picw-terms" data-testid="prompt-intro-terms">
      <v-icon size="20" icon="mdi-file-document-check-outline" class="picw-terms-ico"/>
      <span>{{ termsLine }}</span>
    </div>

    <!-- A run of one needs no list: it would only repeat the title. -->
    <div v-if="steps.length > 1" class="picw-steps">
      <div class="picw-steps-head">What we will ask, in order</div>
      <ol>
        <li v-for="(step, i) in steps" :key="i" class="picw-step">
          <span class="picw-num">{{ i + 1 }}</span>
          <span class="picw-step-title">{{ step.title }}</span>
          <span v-if="step.optional" class="picw-tag">Optional</span>
        </li>
      </ol>
    </div>

    <v-btn
        block
        rounded="pill"
        size="large"
        color="primary"
        variant="flat"
        class="picw-go"
        :loading="busy"
        @click="act('continue')">
      {{ required ? 'Continue' : 'Start' }}
    </v-btn>
    <div v-if="!required" class="picw-later">
      <v-btn variant="text" size="small" :disabled="busy" @click="act('later')">Not now</v-btn>
      <v-btn variant="text" size="small" :disabled="busy" @click="act('never')">No thanks</v-btn>
    </div>
    <slot name="footer"/>
  </div>

  <!-- The optional nudge over a page that still works. -->
  <div v-else class="pic" :class="{'pic--docked': !inert, 'pic--inert': inert}">
    <div class="pic-top">
      <span class="pic-ico" :class="{'pic-ico--req': required}">
        {{ required ? '!' : '?' }}
      </span>
      <div class="pic-words">
        <div class="pic-title">{{ title }}</div>
        <div v-if="line" class="pic-line">{{ line }}</div>
      </div>
      <!-- Only an optional card can be closed. A required one has no x because
           there is nothing behind it to go back to. -->
      <button
          v-if="!required"
          type="button"
          class="pic-x"
          aria-label="No thanks, don't ask again"
          @click="act('never')">&times;</button>
    </div>

    <div class="pic-acts">
      <v-btn v-if="!required" variant="text" size="small" :disabled="busy" @click="act('later')">
        Not now
      </v-btn>
      <v-btn color="primary" variant="flat" size="small" :loading="busy" @click="act('continue')">
        {{ required ? 'Continue' : 'Sure' }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* ── the wall ── */
.picw { text-align: center; }

.picw-logo {
  width: 64px; height: 64px; border-radius: 20px; overflow: hidden;
  margin: 0 auto 18px;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
}

.picw-title {
  font-size: 22px !important; font-weight: 800; letter-spacing: -0.5px; line-height: 1.3;
  margin: 0;
}

.picw-line {
  font-size: 15px; line-height: 1.55; margin: 8px auto 0; max-width: 34ch;
  color: rgb(var(--v-theme-on-surface), .72);
}

.picw-terms {
  display: flex; align-items: flex-start; gap: 10px;
  text-align: left;
  margin-top: 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), .07);
  border: 1px solid rgba(var(--v-theme-primary), .22);
  font-size: 14px; line-height: 1.5; font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}
.picw-terms-ico { color: rgb(var(--v-theme-primary)); flex: none; margin-top: 1px; }

.picw-steps { text-align: left; margin-top: 20px; }
.picw-steps-head {
  font-size: 11.5px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
  color: rgb(var(--v-theme-on-surface), .55);
  margin-bottom: 8px;
}
.picw-steps ol { list-style: none; padding: 0; margin: 0; }
.picw-step {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 0;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
.picw-step:first-child { border-top: 0; padding-top: 4px; }
.picw-num {
  width: 26px; height: 26px; border-radius: 50%; flex: none;
  display: grid; place-items: center;
  font-size: 13px; font-weight: 700;
  background: rgba(var(--v-theme-primary), .11);
  color: rgb(var(--v-theme-primary));
}
.picw-step-title { flex: 1; min-width: 0; font-size: 15px; font-weight: 600; }
.picw-tag {
  flex: none; font-size: 12px; padding: 2px 8px; border-radius: 999px;
  background: rgb(var(--v-theme-on-surface), .06);
  color: rgb(var(--v-theme-on-surface), .62);
}

.picw-go { margin-top: 24px; }
.picw-later { display: flex; justify-content: center; gap: 4px; margin-top: 8px; }

/* ── the docked nudge ── */
.pic {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), .16);
  border-radius: 12px;
  padding: 14px 15px;
}

/* Over a page that still works. Below the assistant panel (2500), which is a
   thing someone opened on purpose and should stay on top of a nudge. */
.pic--docked {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: min(300px, calc(100vw - 32px));
  z-index: 2400;
  box-shadow: 0 18px 44px rgba(0, 0, 0, .18), 0 3px 10px rgba(0, 0, 0, .08);
}

.pic--inert { pointer-events: none; }

.pic-top { display: flex; align-items: flex-start; gap: 10px; }

.pic-ico {
  width: 28px; height: 28px; flex: none;
  border-radius: 8px;
  display: grid; place-items: center;
  font-weight: 700; font-size: 14px;
  background: rgba(var(--v-theme-primary), .11);
  color: rgb(var(--v-theme-primary));
}

.pic-words { min-width: 0; }
.pic-title { font-weight: 600; font-size: 14.5px; line-height: 1.3; }
.pic-line { font-size: 12.5px; line-height: 1.45; margin-top: 3px; color: rgb(var(--v-theme-on-surface), .7); }

.pic-x {
  margin-left: auto; flex: none;
  background: none; border: 0; cursor: pointer;
  font-size: 19px; line-height: 1;
  padding: 0 2px;
  color: rgb(var(--v-theme-on-surface), .55);
}
.pic-x:hover { color: rgb(var(--v-theme-on-surface), .9); }

.pic-acts { display: flex; justify-content: flex-end; gap: 6px; margin-top: 14px; flex-wrap: wrap; }

@media (max-width: 599px) {
  .pic--docked { right: 12px; left: 12px; bottom: 12px; width: auto; }
}
</style>
